import { apiListCustomers, apiSaveCustomer } from "./authApi";
import {
  listPersistedCustomerSyncQueue,
  removePersistedCustomerSyncQueueItem,
  writePersistedCustomerSnapshot,
} from "./offlineErp";

// Fila de sincronizacao de clientes criados/editados offline. Extraido para
// um modulo partilhado para que a mesma logica possa ser acionada tanto pela
// pagina de Clientes (enquanto aberta) como por um efeito global em
// erpContext.js (sempre montado durante a sessao), evitando que clientes
// pendentes fiquem por sincronizar so porque o utilizador navegou para outra
// pagina antes da ligacao voltar. Ao contrario do catalogo de produtos, este
// fluxo nao tem deteccao de conflitos, por isso e seguro correr em segundo
// plano sem UI dedicada.
const CUSTOMER_OFFLINE_SYNC_LOCK_KEY = "angolabi.erp.offline.customers.sync.lock";
const CUSTOMER_OFFLINE_SYNC_LOCK_TTL_MS = 15_000;

function tryAcquireLock(ownerId) {
  if (typeof window === "undefined") {
    return true;
  }

  const now = Date.now();

  try {
    const raw = window.localStorage.getItem(CUSTOMER_OFFLINE_SYNC_LOCK_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const expiresAt = Number(parsed?.expiresAt || 0);
      const owner = String(parsed?.owner || "");
      if (owner && owner !== ownerId && expiresAt > now) {
        return false;
      }
    }

    window.localStorage.setItem(CUSTOMER_OFFLINE_SYNC_LOCK_KEY, JSON.stringify({
      owner: ownerId,
      expiresAt: now + CUSTOMER_OFFLINE_SYNC_LOCK_TTL_MS,
    }));

    return true;
  } catch {
    return true;
  }
}

function releaseLock(ownerId) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const raw = window.localStorage.getItem(CUSTOMER_OFFLINE_SYNC_LOCK_KEY);
    if (!raw) {
      return;
    }

    const parsed = JSON.parse(raw);
    if (String(parsed?.owner || "") === ownerId) {
      window.localStorage.removeItem(CUSTOMER_OFFLINE_SYNC_LOCK_KEY);
    }
  } catch {
    // Ignora erros de lock em storage local para manter a sincronizacao resiliente.
  }
}

/**
 * Cria um "runner" que drena a fila de clientes pendentes para um dado
 * companyUuid/storeUuid. Nao mexe em nenhum estado de UI diretamente — devolve
 * a lista de itens processados para o chamador decidir o que fazer (ex.:
 * atualizar a lista de clientes na pagina, ou nao fazer nada se estiver a
 * correr em segundo plano noutra pagina).
 */
export function createCustomerOfflineSyncRunner(ownerId) {
  let inFlight = false;

  return async function run({ authToken, companyUuid, storeUuid, isOnline = true }) {
    if (!authToken || !companyUuid || isOnline === false) {
      return { processed: [], error: null };
    }

    if (inFlight) {
      return { processed: [], error: null };
    }

    if (!tryAcquireLock(ownerId)) {
      return { processed: [], error: null };
    }

    inFlight = true;
    const processed = [];

    try {
      while (true) {
        const currentQueue = await listPersistedCustomerSyncQueue(companyUuid);
        const queueItem = (currentQueue || []).find((item) => {
          const queueStatus = item?.status || "pending";
          return queueStatus === "pending" && item?.type === "saveCustomer" && item?.companyUuid === companyUuid;
        });

        if (!queueItem) {
          break;
        }

        const response = await apiSaveCustomer(authToken, queueItem.payload);
        const syncedCustomer = response?.item || null;
        await removePersistedCustomerSyncQueueItem(queueItem?.id);
        processed.push({ queueItem, syncedCustomer });
      }

      let refreshedItems = null;
      if (processed.length > 0) {
        try {
          const refreshed = await apiListCustomers(authToken, { companyUuid, storeUuid });
          refreshedItems = refreshed?.items || [];
          await writePersistedCustomerSnapshot({ companyUuid, storeUuid }, { items: refreshedItems });
        } catch {
          // O snapshot offline sera atualizado na proxima carga da pagina de clientes; nao e critico aqui.
        }
      }

      return { processed, error: null, refreshedItems };
    } catch (error) {
      return { processed, error, refreshedItems: null };
    } finally {
      inFlight = false;
      releaseLock(ownerId);
    }
  };
}
